/*
 * decaffeinate suggestions:
 * DS102: Remove unnecessary code created because of implicit returns
 * DS104: Avoid inline assignments
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcessExecutionType, ProcessClientOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {processExecutionTypeDisplayName} from 'processes/ProcessConstants';
import {getLegacyRequestObjectName} from 'requests/RequestConstants';
import {getWorkflowDisplayString} from 'workflows/util/WorkflowUtil';

// lazy for now...
export function typeToName(modelType: string): string {
  switch (modelType) {
    case ModelTypeConstants.ANALYSIS:
      return 'analysis';
    case ModelTypeConstants.ANALYSIS_TEMPLATE:
      return 'analysis template';
    case ModelTypeConstants.ANALYTICS_DASHBOARD:
      return 'dashboard';
    case ModelTypeConstants.ASSAY_RESULT:
      return 'result';
    case ModelTypeConstants.ASSAY_RUN:
      return 'run';
    case ModelTypeConstants.ASSAY_RUN_TASK:
      return processExecutionTypeDisplayName(
        ProcessClientOrigin.ASSAYS,
        ProcessExecutionType.PROCEDURE
      ).toLowerCase();
    case ModelTypeConstants.ASSAY_UNIT_OPERATION_TASK:
      return processExecutionTypeDisplayName(
        ProcessClientOrigin.ASSAYS,
        ProcessExecutionType.PROCEDURE_METHOD
      ).toLowerCase();
    case ModelTypeConstants.ASSAY_STEP_TASK:
      return processExecutionTypeDisplayName(
        ProcessClientOrigin.ASSAYS,
        ProcessExecutionType.PROCEDURE_STEP
      ).toLowerCase();
    case ModelTypeConstants.BIOENTITY_MIXIN:
      return 'entity';
    case ModelTypeConstants.BLOB:
      return 'blob';
    case ModelTypeConstants.BASIC_FOLDER_ITEM:
      return 'custom entity';
    case ModelTypeConstants.BULK_ASSEMBLY:
      return 'assembly';
    case ModelTypeConstants.PROTEIN:
      return 'AA sequence';
    case ModelTypeConstants.SEQUENCE:
      return 'DNA sequence';
    case ModelTypeConstants.RNA_SEQUENCE:
      return 'RNA sequence';
    case ModelTypeConstants.DNA_OLIGO:
      return 'DNA oligo';
    case ModelTypeConstants.RNA_OLIGO:
      return 'RNA oligo';
    case ModelTypeConstants.TAG_SCHEMA:
      return 'entity schema';
    case ModelTypeConstants.SCHEMA_FIELD_SELECTOR:
      return 'dropdown';
    case ModelTypeConstants.SCHEMA_FIELD_SELECTOR_OPTION:
      return 'dropdown option';
    case ModelTypeConstants.STAGE_ENTRY:
      return getWorkflowDisplayString('legacy workflow entry', 'workflow entry');
    case ModelTypeConstants.REQUEST:
      return getLegacyRequestObjectName({lowerCase: true});
    case ModelTypeConstants.SAVED_SEARCH:
      return 'saved search';
    case ModelTypeConstants.SEQUENCE_ANALYSIS:
      return 'DNA / RNA alignment';
    case ModelTypeConstants.STORABLE:
      return 'inventory item';
    case ModelTypeConstants.ASSAY_PROCEDURE:
      return 'assay';
    case ModelTypeConstants.RECIPE_PROCEDURE:
      return 'recipe';
    case ModelTypeConstants.PROTEIN_ALIGNMENT:
      return 'AA alignment';
    case ModelTypeConstants.PROCESS:
      return 'workflow task group';
    case ModelTypeConstants.PROCESS_TASK:
      return 'workflow task';
    case ModelTypeConstants.PROCESS_OUTPUT:
      return 'workflow output';
    case ModelTypeConstants.RECIPE_PROCESS:
      return 'recipe task group';
    case ModelTypeConstants.ASSAY_PROCESS:
      return 'assay task group';
    case ModelTypeConstants.RECIPE_RUN_TASK:
      return processExecutionTypeDisplayName(
        ProcessClientOrigin.RECIPES,
        ProcessExecutionType.PROCEDURE
      ).toLowerCase();
    case ModelTypeConstants.RECIPE_UNIT_OPERATION_TASK:
      return processExecutionTypeDisplayName(
        ProcessClientOrigin.RECIPES,
        ProcessExecutionType.PROCEDURE_METHOD
      ).toLowerCase();
    case ModelTypeConstants.RECIPE_STEP_TASK:
      return processExecutionTypeDisplayName(
        ProcessClientOrigin.RECIPES,
        ProcessExecutionType.PROCEDURE_STEP
      ).toLowerCase();
    case ModelTypeConstants.WORKFLOW:
      return getWorkflowDisplayString('legacy workflow', 'workflow');
    case ModelTypeConstants.TEMPLATE:
      return 'entry template';
    case ModelTypeConstants.SUBTEMPLATE:
      return 'sub-template';
    case ModelTypeConstants.FORM_DEFINITION:
      return 'form template';
    case ModelTypeConstants.FILE:
      return 'file';
    case ModelTypeConstants.SCHEMA_INTERFACE:
      return BenchlingConfig.SCHEMAS_RENAME_INTERFACE_TO_FIELDSET_AND_CATEGORY
        ? 'category'
        : 'schema interface';
    case ModelTypeConstants.REQUEST_V2_DEFINITION:
      return 'request template';
    case ModelTypeConstants.REQUEST_V2_SUBMISSION:
      return 'request';
    case ModelTypeConstants.TEST_DEFINITION:
      return 'test definition';
    case ModelTypeConstants._DO_NOT_USE_ANTIBODY:
      return 'protein';
    default:
      return modelType.replace(/_/g, ' ');
  }
}

export function typeToPluralized(modelType: string): string {
  return pluralize(2, typeToName(modelType));
}

export function typeCountToReadable(typeCount: {[modelType: string]: number}): string {
  // eslint-disable-next-line no-restricted-syntax
  return _.map(typeCount, (count, modelType) => {
    const name = count === 1 ? typeToName(modelType) : typeToPluralized(modelType);
    return `${count} ${name}`;
  }).join(', ');
}

export function formatBytes(numBytes: number, param?: {precision?: number}): string {
  if (param == null) {
    param = {};
  }
  let {precision} = param;
  if (precision == null) {
    precision = 2;
  }
  if (numBytes < 1024) {
    return `${numBytes}B`;
  }
  if (numBytes < 1024 * 1024) {
    return `${Math.ceil(numBytes / 1024)}KB`;
  }
  if (numBytes < 1024 * 1024 * 1024) {
    return `${Math.ceil(numBytes / (1024 * 1024))}MB`;
  }
  const numGB = numBytes / (1024 * 1024 * 1024);

  // Don't add precision if it's an integer number of gigabytes.
  if (numGB % 1 === 0) {
    return `${numGB}GB`;
  }
  return `${numGB.toFixed(precision)}GB`;
}

export function formatThousands(number: number | null): string {
  const localeNumber = number != null ? number.toLocaleString(undefined, {useGrouping: true}) : undefined;
  return localeNumber != null ? localeNumber : `${number}`;
}

export function listToReadable(list: Array<string>, joinType: string = 'and'): string {
  if (list.length === 0) {
    return '';
  } else if (list.length === 1) {
    return list[0];
  } else if (list.length === 2) {
    return `${list[0]} ${joinType} ${list[1]}`;
  } else {
    // eslint-disable-next-line no-restricted-syntax
    return `${_.initial(list).join(', ')}, ${joinType} ${_.last(list)}`;
  }
}

export function listToTruncatedReadable(
  list: Array<string>,
  truncateLength: number = 1,
  joinType: string = 'and'
): string {
  assert(truncateLength >= 1, 'Truncate length must be greater or equal to 1');
  if (list.length <= truncateLength) {
    return listToReadable(list, joinType);
  }
  const maybeOxfordComma = truncateLength === 1 ? '' : ',';
  return `${list.slice(0, truncateLength).join(', ')}${maybeOxfordComma} and ${
    list.length - truncateLength
  } more`;
}

export enum EllipsisDirection {
  FRONT = 'FRONT',
  BACK = 'BACK',
  MIDDLE = 'MIDDLE',
}

export function ellipsize(
  text: string,
  cutoff: number,
  direction: EllipsisDirection = EllipsisDirection.BACK
): string {
  if (text.length > cutoff) {
    switch (direction) {
      case EllipsisDirection.FRONT:
        return `...${text.slice(text.length - (cutoff - 3), text.length)}`;
      case EllipsisDirection.MIDDLE: {
        const firstChars = text.slice(0, Math.ceil((cutoff - 3) / 2));
        const lastChars = text.slice(-(cutoff - 3) / 2);
        return `${firstChars}...${lastChars}`;
      }
      case EllipsisDirection.BACK:
      default:
        return `${text.slice(0, cutoff - 3)}...`;
    }
  }
  return text;
}

// Matches Python string capitalize
// 'string like this' -> 'String like this'
export function capitalize(str?: string | null): string {
  if (!str) {
    return '';
  }
  str = str.charAt(0).toUpperCase() + str.substr(1).toLowerCase();
  // Handle capitalizing all of AA/DNA.
  return str.replace(/(^|\s)(aa|dna|rna)($|\s)/i, (match) => match.toUpperCase());
}

// Matches Python string capwords
// 'string like this' -> 'String Like This'
export function capwords(str?: string): string {
  if (!str) {
    return '';
  }
  return str.replace(/\w\S*/g, capitalize);
}

// 'stringLikeThis' -> 'String like this'
export function camelCaseToCapital(str?: string): string {
  if (!str) {
    return '';
  }
  return capitalize(str.replace(/([A-Z])/g, ' $1'));
}

// 'STRING_LIKE_THIS' -> 'string like this'
export function constantCaseToWords(str?: string): string {
  if (!str) {
    return '';
  }
  return str
    .split(/_/)
    .map((word) => word.toLowerCase())
    .join(' ');
}

// 'string_like_this' to stringLikeThis
export function snakeToCamelCase(str?: string): string {
  if (!str) {
    return '';
  }
  return str
    .split('_')
    .map((word, i) => (i === 0 ? word : capitalize(word)))
    .join('');
}

/**
 * Converts camel case string to snake case.
 *
 * @param str camel case string to convert
 * @returns snake case version of the input
 * @example 'stringLikeThis' -> 'string_like_this'
 */
export function camelCaseToSnake(str: string): string {
  return str.replace(/([A-Z])/g, '_$1').toLowerCase();
}

// 'string_like_this' to 'String like this'
export function snakeToCapital(str?: string): string {
  return capitalize(camelCaseToCapital(snakeToCamelCase(str)).trim());
}

// Replaces the following characters with empty spaces: ", . ' _ -" and trims the output string
// e.g. "hello_world" -> "hello_world", "     www.benchling.com____" -> "www benchling com"
function cleanPhrase(phrase: string): string {
  return phrase.replace(/[,.'_-]/g, ' ').trim();
}

// 'string Like This' or 'String like This' -> 'stringLikeThis'
export function phraseToCamelCase(phrase?: string): string {
  if (!phrase) {
    return '';
  }
  const camelizedWord = cleanPhrase(phrase)
    .split(/\s+/)
    .map((word, idx) => {
      const letters = word.toLowerCase().split('');

      return idx === 0
        ? `${letters[0] + letters.slice(1).join('')}`
        : `${letters[0].toUpperCase() + letters.slice(1).join('')}`;
    })
    .join('');

  return camelizedWord;
}

// 'string Like this' or 'String like THIS' -> 'string_like_this'
export function phraseToSnakeCase(phrase?: string): string {
  if (!phrase) {
    return '';
  }
  const snakeCasedPhrase = cleanPhrase(phrase)
    .split(/\s+/)
    .map((word, idx) => word.toLowerCase())
    .join('_');

  return snakeCasedPhrase;
}

// matches python string replace
// 'red fish blue fish', 'fish', 'cat' -> 'red cat blue cat'
export function replaceAll(str: string, oldSubstring: string, newSubstring: string): string {
  const charToReplaceRe = new RegExp(oldSubstring, 'g');
  return str.replace(charToReplaceRe, newSubstring);
}
